
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  HiOutlineChatBubbleLeftRight,
  HiOutlineUserCircle,
} from "react-icons/hi2";

import { media } from "../../server";

/* ============================================
   IMAGE URL HELPER
============================================ */

function getImageUrl(image) {
  if (!image || typeof image !== "string") {
    return null;
  }

  const filename = image.trim();

  if (!filename) return null;

  if (/^https?:\/\//i.test(filename)) {
    return filename;
  }

  const base = String(media || "").replace(/\/+$/, "");
  const path = filename.replace(/^\/+/, "");

  return base ? `${base}/${path}` : `/${path}`;
}

/* ============================================
   TEXT HELPER
============================================ */

function getPlainText(text) {
  if (typeof text !== "string") return "";

  return text
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/* ============================================
   REVIEW CARD
============================================ */

function ReviewsCard({
  reviews,
  isActive = false,
}) {
  const { i18n } = useTranslation();

  const isEnglish = (
    i18n.resolvedLanguage ||
    i18n.language ||
    "ar"
  )
    .toLowerCase()
    .startsWith("en");

  const name = isEnglish
    ? reviews?.titleEn
    : reviews?.titleAr;

  const description = getPlainText(
    isEnglish
      ? reviews?.textEn
      : reviews?.textAr
  );

  const imageUrl = getImageUrl(reviews?.Image);

  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    setImageError(false);
  }, [imageUrl]);

  const showImage = imageUrl && !imageError;

  return (
    <article
      className={`
        relative mx-auto
        flex h-[292px]
        w-full max-w-[470px]
        flex-col overflow-hidden

        rounded-[20px]
        border
        bg-white
        p-6

        transition-all duration-500
        ease-out

        dark:bg-[#182F42]

        sm:p-7

        ${
          isActive
            ? `
              scale-100
              border-[#C19B60]/45
              opacity-100

              shadow-[0_18px_45px_rgba(15,39,55,0.11)]

              dark:border-[#D7B77F]/35
              dark:shadow-[0_20px_45px_rgba(0,0,0,0.18)]
            `
            : `
              scale-[0.94]
              border-[#DCE5EC]
              opacity-70

              shadow-[0_8px_25px_rgba(15,39,55,0.04)]

              dark:border-white/10
              dark:opacity-65
              dark:shadow-none
            `
        }
      `}
    >
      {/* ======================================
          GOLD ACCENT
      ====================================== */}

      <div
        aria-hidden="true"
        className="
          absolute inset-x-0 top-0
          h-[2px]

          bg-gradient-to-r
          from-transparent
          via-[#C19B60]/70
          to-transparent

          dark:via-[#D7B77F]/60
        "
      />

      {/* ======================================
          CARD HEADER
      ====================================== */}

      <div className="flex items-start justify-between gap-4">

        {/* Customer */}
        <div className="flex min-w-0 items-center gap-3">

          {/* Avatar */}
          <div
            className="
              flex h-[54px] w-[54px]
              shrink-0 items-center justify-center

              overflow-hidden
              rounded-full

              border-2 border-[#EDF3F7]
              bg-[#EDF3F7]

              dark:border-white/10
              dark:bg-[#294961]
            "
          >
            {showImage ? (
              <img
                src={imageUrl}
                alt=""
                loading="lazy"
                decoding="async"
                onError={() => setImageError(true)}
                className="h-full w-full object-cover"
              />
            ) : (
              <HiOutlineUserCircle
                size={33}
                aria-hidden="true"
                className="text-[#C19B60]"
              />
            )}
          </div>

          {/* Customer info */}
          <div className="min-w-0">

            <h3
              className="
                font-cairo
                text-sm font-semibold
                leading-7

                text-[#19344D]
                dark:text-[#F5F8FB]

                line-clamp-1
              "
              title={name}
            >
              {name}
            </h3>

            {reviews?.date && (
              <p
                className="
                  caption
                  mt-0.5
                  text-[#7A8D9D]
                  dark:text-[#A9BDCC]
                "
              >
                {reviews.date}
              </p>
            )}

          </div>
        </div>

        {/* Quote Icon */}
        <div
          className="
            flex h-11 w-11
            shrink-0 items-center justify-center

            rounded-xl
            bg-[#F1F5F8]
            text-[#C19B60]

            dark:bg-[#294961]
            dark:text-[#D7B77F]
          "
        >
          <HiOutlineChatBubbleLeftRight
            size={23}
            aria-hidden="true"
          />
        </div>

      </div>

      {/* ======================================
          DIVIDER
      ====================================== */}

      <div
        aria-hidden="true"
        className="
          my-5 h-px w-full
          bg-[#E4EBF0]
          dark:bg-white/10
        "
      />

      {/* ======================================
          REVIEW TEXT
      ====================================== */}

      <div className="relative flex-1 overflow-hidden">

        <p
          className="
            body-2
            line-clamp-4
            text-[#526B7E]
            dark:text-[#C6D3DE]
          "
        >
          {description}
        </p>

      </div>

      {/* ======================================
          CARD FOOTER
      ====================================== */}

      <div className="mt-auto flex items-center gap-2 pt-4">

        <span
          aria-hidden="true"
          className="
            h-1.5 w-1.5
            rounded-full
            bg-[#C19B60]
            dark:bg-[#D7B77F]
          "
        />

        <span
          aria-hidden="true"
          className="
            h-px w-8
            bg-[#C19B60]/50
            dark:bg-[#D7B77F]/50
          "
        />

      </div>

    </article>
  );
}

export default ReviewsCard;
